import React, { useEffect, useState } from 'react';
import { 
  Button,
  Col,
  Form,
  Row,
  Table,
  Spinner,
  Badge,
  Card,
  InputGroup,
  Tooltip,
  OverlayTrigger
} from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { ArrowLeft, Download, FileEarmarkSpreadsheet, InfoCircle, Printer, Search } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';

const ProductSalesReport = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();
  
  const accessToken = localStorage.getItem("authToken");
  const [productList, setProductList] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryData, setSelectedCategoryData] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedOutletName, setSelectedOutletName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');    
  const [clicked, setClicked] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

  useEffect(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));
    
    setStartDate(start);
    setEndDate(end);
    getOutlets();
    getCategoryList();
  }, []);

  useEffect(() => {
    if (searchText === "" && isTouched === true) {
      console.log("hELLO");
      
      getProductData(startDate, endDate, selectedOutlet, selectedCategory);
    }
  }, [searchText]);

  const handleDownloadExcel = async () => {
    setDownloadBtnLoading(true);
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    getProductData(adjustedStartDate, adjustedEndDate, selectedOutlet, selectedCategory, true);
  };

  const getProductData = async (startDate, endDate, outlet, category, download) => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const updatedStartDate = startDate ? startDate.toISOString().split("T")[0] : null;
      const updatedEndDate = endDate ? endDate.toISOString().split("T")[0] : null;

      const queryParams = new URLSearchParams();
      if (updatedStartDate) queryParams.append('startDate', updatedStartDate);
      if (updatedEndDate) queryParams.append('endDate', updatedEndDate);
      if (outlet) queryParams.append('outlet', outlet);
      if (category) queryParams.append('category', category);
      if (download) queryParams.append('isDownload', download);

      const response = await axios.get(`order/getProductsReportForAdmin?${queryParams.toString()}`, { 
        headers,
        responseType: download ? 'blob' : 'json'
      });
      
      if (download) {
          // Create a blob from the response data
          const blob = new Blob([response.data], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          // Create a link element
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          
          // Set the filename for the download
          const fileName = `${selectedOutletName} Product Sales Report (${updatedStartDate} to ${updatedEndDate}).xlsx`;
          link.download = fileName;
          
          // Append the link to the body and trigger the download
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
  
          toast.success('Excel file downloaded successfully!', {
              position: "top-right",
              autoClose: 3000,
              theme: "light"
          });
      } else {
        setProductList(response?.data?.data || []);
        setFilteredData(response?.data?.data || []);
      }
      
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
      setDownloadBtnLoading(false);
    }
  };

  const getOutlets = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("outlet", { headers });

      if (response?.data?.status === true) {
        const filteredOutlets = response?.data?.data?.outlets.filter((act) => act?.status === "ACTIVE" || "INACTIVE");
        setOutlets(filteredOutlets);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const getCategoryList = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/category/get", { headers });

      if (response?.data?.status === true) {
        setCategoryList(response?.data?.data?.categories || []);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    let errorMessage = 'An error occurred';
    if (error.response?.status === 406) {
      errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
    } else if (error.response?.status === 412) {
      errorMessage = error.response?.data?.data?.message;
    } else if (error.response?.status === 500) {
      errorMessage = error.response?.data?.error;
    }

    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  };

  const handleEndDateChange = (date) => {
    if (date && startDate && date < startDate) {
      setError('End date cannot be earlier than start date');
    } else {
      setError('');
      setEndDate(date);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setClicked(true);
    setBtnLoading(true);
    
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    
    await getProductData(adjustedStartDate, adjustedEndDate, selectedOutlet, selectedCategory);
    
    toast.success("Data Fetched Successfully", {
      position: "top-right",
      autoClose: 3000,
      theme: "colored",
    });
    setBtnLoading(false);
  };

  // const handlePrintReport = () => {
  //   const formatDate = (date) => {
  //     return new Date(date).toLocaleDateString('en-GB', {
  //       day: '2-digit',
  //       month: '2-digit',
  //       year: 'numeric',
  //     });
  //   };
    
  //   const formattedStartDate = formatDate(startDate);
  //   const formattedEndDate = formatDate(endDate);
    
  //   const totalSales = filteredData.reduce((total, product) => total + (product?.totalQuantity || 0), 0);
  //   const totalDisc = filteredData.reduce((total, product) => total + (product?.totalDiscount || 0), 0).toFixed(2);
  //   const totaltax = filteredData.reduce((total, product) => total + (product?.totalTaxAmount || 0), 0).toFixed(2);
  //   const subTotal = filteredData.reduce((total, product) => {
  //     const revenue = product?.totalRevenue || 0;
  //     const tax = product?.totalTaxAmount || 0;
  //     return total + (revenue - tax);
  //   }, 0).toFixed(2);
  //   const totalRevenue = filteredData.reduce((total, product) => total + (product?.totalRevenue || 0), 0).toFixed(2);

  //   const tableContent = `
  //     <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
  //       <thead>
  //         <tr style="background-color: #f8f9fa;">
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">#</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Category & Product</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Type</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">MRP</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Rate</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Avg Rate</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Sales</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Before Dis Taxable</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Discount</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">After Dis Taxable</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Tax</th>
  //           <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Total</th>
  //         </tr>
  //       </thead>
  //       <tbody>
  //         ${filteredData?.length > 0 ? 
  //           filteredData.map((product, index) => `
  //             <tr>
  //               <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px;">
  //                 <strong style="color: #6f42c1;">${product?.categoryName}</strong> > 
  //                 <span style="color: #3498db;">${product?.product?.name}</span>
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; color: ${product?.product?.foodType === "NON-VEG" ? '#dc3545' : '#28a745'}">
  //                 ${product?.product?.foodType}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
  //                 ${product?.product?.orderInfo?.mrpRate ? '₹ ' + product.product.orderInfo.mrpRate.toFixed(2) : '----'}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
  //                 ${product?.product?.orderInfo?.rate ? '₹ ' + product.product.orderInfo.rate.toFixed(2) : '----'}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
  //                 ${product?.product?.orderInfo?.rate ? '₹ ' + product.product.orderInfo.rate.toFixed(2) : '----'}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${product?.totalQuantity || 0}</td><td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
  //                 ₹ ${((product?.totalRevenue || 0) - (product?.totalTaxAmount || 0)).toFixed(2)}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${product?.totalDiscount?.toFixed(2) || '0.00'}</td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
  //                 ₹ ${((product?.totalRevenue || 0) - (product?.totalTaxAmount || 0)).toFixed(2)}
  //               </td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${product?.totalTaxAmount?.toFixed(2) || '0.00'}</td>
  //               <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${product?.totalRevenue?.toFixed(2) || '0.00'}</td>
  //             </tr>
  //           `).join("") : `
  //             <tr>
  //               <td colspan="12" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No products found</td>
  //             </tr>`
  //         }
  //         ${filteredData?.length > 0 ? `
  //           <tr style="font-weight: bold; background-color: #f8f9fa;">
  //             <td colspan="5" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">TOTAL</td>
  //             <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${totalSales}</td>
  //             <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalDisc}</td>
  //             <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totaltax}</td>
  //             <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${subTotal}</td>
  //             <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalRevenue}</td>
  //           </tr>` : ''}
  //       </tbody>
  //     </table>
  //   `;

  //   const win = window.open("", "_blank");
  //   win.document.write(`
  //     <html>
  //       <head>
  //         <title>Product Sales Report</title>
  //         <style>
  //           body { font-family: Arial, sans-serif; margin: 20px; }
  //           .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
  //           .title { font-size: 18px; font-weight: bold; }
  //           .date-range { font-size: 14px; color: #666; }
  //           table { width: 100%; border-collapse: collapse; }
  //           th { background-color: #f8f9fa; text-align: left; }
  //         </style>
  //       </head>
  //       <body onload="window.print();window.close()">
  //         <div class="header">
  //           <div class="title">Product Sales Report</div>
  //           <div class="date-range">${formattedStartDate} to ${formattedEndDate}</div>
  //         </div>
  //         ${tableContent}
  //       </body>
  //     </html>
  //   `);
  //   win.document.close();
  // };

  const handlePrintReport = () => {
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };
    
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);
    
    const totalSales = filteredData.reduce((total, product) => total + (product?.totalQuantity || 0), 0);
    const totalAddonPrice = filteredData.reduce((total, product) => total + (Number(product?.addonOfferPrice) || 0), 0).toFixed(2);
    const totalActualTaxable = filteredData.reduce((total, product) => total + (Number(product?.actualTaxable) || 0), 0).toFixed(2);
    const totalDisc = filteredData.reduce((total, product) => total + (Number(product?.actualTaxable || 0) - Number(product?.totalTaxable || 0)), 0).toFixed(2);
    const totalAfterDiscountTaxable = filteredData.reduce((total, product) => total + (Number(product?.totalTaxable) || 0), 0).toFixed(2);

    const tableContent = `
      <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 50px;">#</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Category & Product</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px;">Type</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Rate</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Avg. Price</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: center;">Sales</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 100px; text-align: right;">Addon Price</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">Actual Taxable</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">Discount</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; width: 120px; text-align: right;">After Dis. Taxable</th>
          </tr>
        </thead>
        <tbody>
          ${filteredData?.length > 0 ? 
            filteredData.map((product, index) => `
              <tr>
                <td style="border: 1px solid #dee2e6; padding: 8px; color: #6c757d;">${index + 1}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">
                  <div style="color: #212529;">${product?.productName || '----'}</div>
                  <div style="font-weight: 600; color: #0d6efd;">${product?.categoryName || '----'}</div>
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">
                  <span style="display: inline-block; padding: 0.25em 0.4em; font-size: 75%; font-weight: 700; line-height: 1; text-align: center; white-space: nowrap; vertical-align: baseline; border-radius: 0.25rem; text-transform: uppercase; background-color: ${product?.foodType === "NON-VEG" ? '#dc3545' : '#198754'}; color: white;">
                    ${product?.foodType}
                  </span>
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ${product?.rate ? '₹ ' + product.rate.toFixed(2) : '----'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ${product?.averageRateOfProduct ? '₹ ' + product.averageRateOfProduct.toFixed(2) : '----'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${product?.totalQuantity || 0}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.addonOfferPrice?.toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.actualTaxable?.toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${(product?.actualTaxable - product.totalTaxable).toFixed(2) || '0.00'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">
                  ₹ ${product?.totalTaxable?.toFixed(2) || '0.00'}
                </td>
              </tr>
            `).join("") : `
              <tr>
                <td colspan="10" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                  <i style="font-size: 16px; color: #6c757d;"></i>
                  <p style="margin: 8px 0 0; color: #6c757d;">No products found for the selected criteria</p>
                </td>
              </tr>`
          }
          ${filteredData?.length > 0 ? `
            <tr style="font-weight: bold; background-color: #f8f9fa;">
              <td colspan="5" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">TOTAL</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${totalSales}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalAddonPrice}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalActualTaxable}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalDisc}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${totalAfterDiscountTaxable}</td>
            </tr>` : ''}
        </tbody>
      </table>
    `;

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Product Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            .date-range { font-size: 14px; color: #666; }
            table { width: 100%; border-collapse: collapse; }
            th { background-color: #f8f9fa; text-align: left; }
          </style>
        </head>
        <body onload="window.print();window.close()">
          <div class="header">
            <div class="title">Product Sales Report</div>
            <div class="date-range">${formattedStartDate} to ${formattedEndDate}</div>
          </div>
          ${tableContent}
        </body>
      </html>
    `);
    win.document.close();
  };

  const handleSearchProducts = () => {
    const trimmedSearchText = searchText.trim().toLowerCase();
    if (trimmedSearchText) {
      const filtered = productList?.filter(product => 
        product?.productName?.toLowerCase().includes(trimmedSearchText)
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(productList);
    }
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div>
                  <h4 className="mb-0 fw-bold d-inline-block">
                      <i className="bi bi-box-seam me-2"></i>PRODUCT SALES REPORT
                  </h4>
              </div>
              <div>
                  <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => navigate('/reports/all-reports')}
                      className="me-3 d-flex align-items-center fw-semibold"
                      style={{ height: '38px' }}
                  >
                      <ArrowLeft className="me-1" /> Back to Reports
                  </Button>
              </div>
          </div>
          <hr className='mt-0 mb-0' /> 
          
          <div className="card-body">
            {/* Filters Section */}
            <div className="p-3 bg-light rounded-3">
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group controlId="outletSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-shop me-2"></i>Outlet
                      </Form.Label>
                      <Form.Select
                        value={selectedOutlet}
                        onChange={(e) => 
                          {
                            setSelectedOutlet(e.target.value);
                            setSelectedOutletName(e.target.selectedOptions[0].label)
                          }
                        }
                        className="border-secondary"
                      >
                        <option value="">All Outlets</option>
                        {outlets?.map((outlet) => (
                          <option key={outlet?._id} value={outlet?._id} label={`${outlet?.name} (${outlet?.city})`}>
                            {outlet?.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="categorySelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-tags me-2"></i>Category
                      </Form.Label>
                      <Form.Select 
                        value={selectedCategory} 
                        onChange={(e) => 
                            setSelectedCategory(e.target.value)
                        }
                        className="border-secondary"
                      >
                        <option value="">All Categories</option>
                        {categoryList.map((category) => (
                          <option key={category._id} value={category._id} label={category?.name}>
                            {category.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* <Col md={2}>
                    <Form.Group controlId="startDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>Start Date
                      </Form.Label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd-MM-yyyy"
                        className="form-control border-secondary"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="endDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>End Date
                      </Form.Label>
                      <DatePicker
                        selected={endDate}
                        onChange={handleEndDateChange}
                        dateFormat="dd-MM-yyyy"
                        minDate={startDate}
                        className="form-control border-secondary"
                      />
                      {error && <div className="text-danger small mt-1">{error}</div>}
                    </Form.Group>
                  </Col> */}
                  <Col md={2}>
                    <Form.Group controlId="startDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>Start Date
                      </Form.Label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          // Reset end date if it's outside the 60-day window
                          if (endDate && date && endDate > new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                            setEndDate(null);
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        className="form-control border-secondary"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="endDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>End Date
                      </Form.Label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                          if (startDate && date > new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                            setError("End date cannot be more than 60 days after start date");
                          } else {
                            setError("");
                            handleEndDateChange(date);
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        minDate={startDate}
                        maxDate={startDate ? new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000) : null}
                        className="form-control border-secondary"
                      />
                      {error && <div className="text-danger small mt-1">{error}</div>}
                    </Form.Group>
                  </Col>


                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={!startDate || !endDate}
                    >
                      {btnLoading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>

            {/* Search and Actions Section */}
            <div className="mb-4 p-3 bg-light rounded-3">
              <Row className="g-3 align-items-center">
                <Col md={6}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search by product name"
                      value={searchText}
                      onChange={(e) => 
                        {
                          setSearchText(e.target.value)
                          setIsTouched(true);
                        }
                      }
                      className="border-secondary"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={handleSearchProducts}
                      disabled={!searchText.trim()}
                    >
                      <Search /> Search
                    </Button>
                  </InputGroup>
                </Col>
                
                <Col md={6}>
                  <Button
                    variant="outline-success"
                    className='fw-semibold w-50'
                    onClick={handleDownloadExcel}
                    disabled={!filteredData.length}
                  >
                    <FileEarmarkSpreadsheet className="me-2" />
                    { downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel' }
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Results Section */}
            {!clicked ? (
              <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                <div className="text-center">
                  <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                  <h5 className="text-muted mt-3">
                    Please select filters and generate report
                  </h5>
                </div>
              </div>
            ) : (
              <div className="table-responsive border rounded-3">
                <Table striped bordered hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th width="50">#</th>
                      <th>Category & Product</th>
                      <th width="100">Type</th>
                      <th width="100" className="text-end">Rate</th>
                      <th width="100" className="text-end">Avg. Price</th>
                      <th width="100" className="text-center">Sales</th>
                      <th width="100" className="text-end">Addon Price</th>
                      <th width="120" className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          Actual Taxable
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="actual-taxable-tooltip">
                                (Average Price * Sales Count)
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }} className="ms-1">
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>
                      <th width="120" className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          Discount
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="discount-tooltip">
                                (Before Discount Taxable - After Discount Taxable)
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }} className="ms-1">
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>
                      <th width="120" className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          After Dis. Taxable
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="after-discount-tooltip">
                                Taxable amount after applying all discounts
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }} className="ms-1">
                              <InfoCircle className="text-muted" size={14} />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2 mb-0">Loading product data...</p>
                        </td>
                      </tr>
                    ) : filteredData?.length > 0 ? (
                      <>
                        {filteredData?.map((product, index) => (
                          <tr key={index}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <div className="text-dark">{product?.productName}</div>
                              <div className="fw-semibold text-primary">{product?.categoryName}</div>
                            </td>
                            <td>
                              <Badge 
                                bg={product?.foodType === "NON-VEG" ? "danger" : "success"}
                                className="text-uppercase"
                              >
                                {product?.foodType}
                              </Badge>
                            </td>
                            {/* <td className="text-end">
                              {product?.mrpRate ? 
                                `₹ ${product.product.orderInfo.mrpRate.toFixed(2)}` : '----'}
                            </td> */}
                            <td className="text-end">
                              {product?.rate ? 
                                `₹ ${product.rate.toFixed(2)}` : '----'}
                            </td>
                            <td className="text-end">
                              {product?.averageRateOfProduct ? 
                                `₹ ${product?.averageRateOfProduct?.toFixed(2)}` : '----'}
                            </td>
                            <td className="text-center">{product?.totalQuantity}</td>
                            <td className="text-end">₹ {product?.addonOfferPrice?.toFixed(2)}</td>
                            <td className="text-end">₹ {product?.actualTaxable?.toFixed(2)}</td>
                            <td className="text-end">
                              ₹ {(product?.actualTaxable - product.totalTaxable).toFixed(2)}
                            </td>
                            <td className="text-end">₹ {product?.totalTaxable?.toFixed(2)}</td>
                            {/* <td className="text-end">₹ {product?.totalTaxAmount?.toFixed(2)}</td> */}
                            {/* <td className="text-end fw-semibold text-success">
                              ₹ {product?.totalRevenue?.toFixed(2)}
                            </td> */}
                          </tr>
                        ))}
                        <tr className="bg-light">
                          <td colSpan="5" className="text-end fw-bold">TOTAL</td>
                          <td className="text-center fw-bold">
                            {filteredData.reduce((total, product) => total + (product?.totalQuantity || 0), 0)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.addonOfferPrice || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.actualTaxable || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + ((product.actualTaxable - product.totalTaxable) || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold">
                            ₹ {filteredData.reduce((total, product) => total + (product?.totalTaxable || 0), 0).toFixed(2)}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                          <p className="mt-2 mb-0 text-muted">
                            No products found for the selected criteria
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductSalesReport;